import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Smartphone, Copy, Download } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Field, btnGhost, btnPrimary, inputCls } from "../../components/ui";
import { ChangePassword } from "../../components/ChangePassword";

function RecoveryCodes({ codes, onClose }) {
  const text = codes.join("\n");
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([`Códigos de recuperação BÔ Market\n\n${text}\n`], { type: "text/plain" }));
    a.download = "bomarket-codigos-recuperacao.txt"; a.click();
  };
  return (
    <div className="flex flex-col gap-3" data-testid="mfa-recovery-codes">
      <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Guarde estes códigos num local seguro. Cada código só pode ser usado uma vez e não voltarão a ser mostrados.</p>
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-50 p-4 font-mono text-sm">{codes.map((c) => <span key={c} data-testid="mfa-recovery-code">{c}</span>)}</div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => navigator.clipboard?.writeText(text).then(() => toast.success("Códigos copiados"))} className={btnGhost} data-testid="mfa-copy-codes-btn"><Copy className="h-4 w-4" />Copiar</button>
        <button type="button" onClick={download} className={btnGhost} data-testid="mfa-download-codes-btn"><Download className="h-4 w-4" />Descarregar</button>
        {onClose && <button type="button" onClick={onClose} className={btnPrimary} data-testid="mfa-codes-done-btn">Guardei os códigos</button>}
      </div>
    </div>
  );
}

export function MfaSetup({ onDone }) {
  const { setUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const start = async () => {
    try { setSetup((await api.post("/auth/mfa/setup")).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const enable = async (e) => {
    e.preventDefault();
    try { setResult((await api.post("/auth/mfa/enable", { code: code.trim() })).data); toast.success("Autenticação multifator ativada"); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const close = () => { setUser(result.user); onDone?.(); };
  if (result) return <div className="bo-card p-5"><h3 className="mb-3 font-bold">Códigos de recuperação</h3><RecoveryCodes codes={result.recovery_codes} onClose={close} /></div>;
  return (
    <div className="bo-card flex flex-col gap-4 p-5" data-testid="mfa-setup">
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-bo-green text-bo-yellow"><Smartphone className="h-5 w-5" /></span>
        <div><h3 className="font-bold">Configurar a aplicação autenticadora</h3>
          <p className="text-sm text-gray-500">Use Google Authenticator, Microsoft Authenticator ou Authy. A verificação em dois passos é opcional, mas recomendada.</p></div>
      </div>
      {!setup ? <button onClick={start} className={`${btnPrimary} self-start`} data-testid="mfa-start-btn"><KeyRound className="h-4 w-4" />Iniciar configuração</button> : (
        <form onSubmit={enable} className="grid gap-4 sm:grid-cols-[auto,1fr]">
          <img src={setup.qr} alt="Código QR para a aplicação autenticadora" className="h-60 w-60 rounded-xl border border-gray-100 bg-white p-2" data-testid="mfa-qr" />
          <div className="flex flex-col gap-3">
            <p className="text-sm">1. Leia o código QR com a aplicação. Se não conseguir, introduza manualmente esta chave:</p>
            <code className="break-all rounded-lg bg-gray-50 px-3 py-2 text-xs" data-testid="mfa-secret">{setup.secret}</code>
            <Field label="2. Código de 6 dígitos"><input required inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className={`${inputCls} text-center tracking-[0.3em]`} data-testid="mfa-enable-code-input" /></Field>
            <button disabled={code.trim().length !== 6} className={`${btnPrimary} self-start`} data-testid="mfa-enable-btn"><ShieldCheck className="h-4 w-4" />Ativar</button>
          </div>
        </form>
      )}
    </div>
  );
}

export function SecurityTab() {
  const [st, setSt] = useState(null);
  const [codes, setCodes] = useState(null);
  const load = () => api.get("/auth/mfa/status").then(({ data }) => setSt(data));
  useEffect(() => { load(); }, []);
  const regenerate = async () => {
    try { setCodes((await api.post("/auth/mfa/recovery-codes")).data.recovery_codes); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!st) return null;
  if (!st.enabled) return <div className="flex flex-col gap-5"><ChangePassword /><MfaSetup onDone={load} /></div>;
  return (
    <div className="flex flex-col gap-5"><ChangePassword />
    <div className="bo-card flex flex-col gap-4 p-5" data-testid="security-tab">
      <p className="flex items-center gap-2 font-semibold text-bo-green" data-testid="mfa-status-enabled"><ShieldCheck className="h-5 w-5" />Autenticação multifator ativa</p>
      <p className="text-sm text-gray-600" data-testid="mfa-recovery-left">Códigos de recuperação disponíveis: <strong>{st.recovery_left}</strong></p>
      {codes ? <RecoveryCodes codes={codes} onClose={() => setCodes(null)} /> :
        <button onClick={regenerate} className={`${btnGhost} self-start`} data-testid="mfa-regenerate-btn"><KeyRound className="h-4 w-4" />Gerar novos códigos de recuperação</button>}
    </div></div>
  );
}
